'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { StoreLink } from '@/components/store';
import type { CartItem as CartItemType } from '@cobrastyle/shared-types';
import { Text } from '@cobrastyle/ui';
import { lineDisplayPrice, lineOriginalPrice } from '@/lib/cart-line-price';
import type { CartLineDecoration } from '@/lib/cart-lines';
import { quantityRules } from '@/lib/quantity-rules';
import { QuantitySelector } from './quantity-selector';
import { BundleOptionsSummary } from './bundle-options-summary';
import { LineItemPrice } from './line-item-price';
import { useFreeGiftLine } from './free-gift-lines-provider';
import { FreeGiftBadge, FreeGiftChargeWarning, FreeGiftQuantity } from './free-gift-marks';

interface MiniCartItemProps {
  item: CartItemType;
  /**
   * Only the minimum and pack size are read here — fetched after mount by the drawer, since the
   * mini cart has no server render of its own. Absent until then, which is the unrestricted rule.
   */
  decoration?: Pick<CartLineDecoration, 'minOrderQuantity' | 'packSize'>;
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
  isLoading: boolean;
}

/**
 * Compact cart row for the mini-cart drawer. Unlike the full-page `CartItem`,
 * the layout is fixed (not viewport-responsive) so it fits the ~360px drawer
 * regardless of screen size.
 */
export function MiniCartItem({
  item,
  decoration,
  onUpdateQuantity,
  onRemove,
  isLoading,
}: MiniCartItemProps) {
  const t = useTranslations('cart');
  const giftLine = useFreeGiftLine(item.uid);
  const rules = quantityRules({
    minOrder: decoration?.minOrderQuantity,
    packSize: decoration?.packSize,
  });
  return (
    <div className="flex gap-3 rounded-xl border border-border bg-card-hover p-3">
      <StoreLink href={`/${item.product.urlKey}`} className="shrink-0">
        <div className="relative h-16 w-16 overflow-hidden rounded-lg bg-placeholder">
          <Image
            src={item.product.thumbnail.url}
            alt={item.product.thumbnail.label || item.product.name}
            fill
            className="object-cover"
            sizes="64px"
          />
        </div>
      </StoreLink>

      <div className="flex min-w-0 flex-1 flex-col">
        {giftLine && <FreeGiftBadge className="mb-1 self-start" />}
        <div className="flex items-start justify-between gap-2">
          <StoreLink
            href={`/${item.product.urlKey}`}
            className="min-w-0 flex-1 truncate text-sm font-medium text-foreground transition-colors hover:text-primary"
          >
            {item.product.name}
          </StoreLink>
          <button
            onClick={onRemove}
            disabled={isLoading}
            className="-mr-1 -mt-1 shrink-0 rounded-lg p-2 text-muted transition-colors hover:bg-card hover:text-foreground disabled:opacity-50"
            aria-label={t('removeItem')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {item.configurableOptions && (
          <Text size="sm" variant="muted" className="mt-0.5 truncate">
            {item.configurableOptions.map((opt) => `${opt.optionLabel}: ${opt.valueLabel}`).join(', ')}
          </Text>
        )}

        {item.bundleOptions && item.bundleOptions.length > 0 && (
          <BundleOptionsSummary options={item.bundleOptions} className="mt-0.5 space-y-0.5" />
        )}

        {giftLine && <FreeGiftChargeWarning line={giftLine} className="mt-1 block" />}

        <div className="mt-2 flex items-center justify-between gap-2">
          {giftLine ? (
            <FreeGiftQuantity quantity={item.quantity} />
          ) : (
            <QuantitySelector
              value={item.quantity}
              onChange={onUpdateQuantity}
              min={rules.min}
              step={rules.step}
              max={rules.max}
              disabled={isLoading}
            />
          )}
          <LineItemPrice
            price={lineDisplayPrice(item.prices)}
            originalPrice={lineOriginalPrice(item.prices)}
            currency={item.prices.currency}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
}
